
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { agentService } from "@/services/agent.service/agent.api";
import { teamService } from "@/services/team.service/team.api";
import { AgentRole, type Agent } from "@/types/team";
import { Loader2, Trash2, UserPlus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import DeleteTeamDialog from "./DeleteTeamDialog";

interface AgentsManagementProps {
  teamId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export default function AgentsManagement({
  teamId,
  onClose,
  onRefresh,
}: AgentsManagementProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<Agent[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<AgentRole[]>([]);
  const [teamAgents, setTeamAgents] = useState<Agent[]>([]);
  const [isAddingAgent, setIsAddingAgent] = useState(false);
  const [isEditingAgent, setIsEditingAgent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<Agent[]>([]);

  // Buscar agentes da equipe
  const fetchTeamAgents = async () => {
    setIsLoading(true);
    try {
      const [response, error] = await teamService.getAgents(teamId);
      if (error) {
        toast.error("Erro ao buscar agentes da equipe");
        return;
      }
      
      if (response) {
        setTeamAgents(response.result || []);
      }
    } catch (error) {
      toast.error("Erro ao buscar agentes da equipe");
    } finally {
      setIsLoading(false);
    }
  };

  // Buscar agentes ao carregar o componente
  useEffect(() => {
    if (teamId) {
      fetchTeamAgents();
    }
  }, [teamId]);

  const handleAddAgent = async () => {
    if (!selectedUser) {
      toast.error("Selecione um usuário");
      return;
    }

    setIsAddingAgent(true);

    // Convertendo o User para Agent
    const agentData: string[] = selectedUser.map((user) => {
      return user._id;
    })

    try {
        const [response, error] = await teamService.addAgent(teamId, agentData);

      if (error) {
        toast.error("Erro ao adicionar agente");
        return;
      }

      if (response) {
        toast.success("Agente adicionado com sucesso!");
        fetchTeamAgents();
        setSelectedUser([]);
        setSelectedPermissions([]);
        onRefresh(); // Atualizar lista de equipes na página principal
      }
    } finally {
      setIsAddingAgent(false);
    }
  };

  const handleUpdateAgent = async (agent: Agent) => {
    setCurrentAgent(agent);
    setSelectedPermissions(agent.permissions);
    setIsEditingAgent(true);
  };

  const saveAgentUpdate = async () => {
    if (!currentAgent || selectedPermissions.length === 0) {
      toast.error("Dados do agente inválidos");
      return;
    }

    setIsAddingAgent(true);

    try {
      const [response, error] = await teamService.updateAgent(
        teamId,
        currentAgent._id,
        { permissions: selectedPermissions }
      );

      if (error) {
        toast.error("Erro ao atualizar agente");
        return;
      }

      if (response) {
        toast.success("Agente atualizado com sucesso!");
        setSelectedUser([]);
        setSelectedPermissions([]);
        setIsEditingAgent(false);
        onRefresh(); // Atualizar lista de equipes na página principal
      }
    } finally {
      setIsAddingAgent(false);
    }
  };

  const handleRemoveAgent = async () => {
    if (!currentAgent) return;
    
    try {
      const [response, error] = await teamService.removeAgent(teamId, currentAgent._id);

      if (error) {
        toast.error("Erro ao remover agente");
        return;
      }

      if (response !== null) {
        toast.success("Agente removido com sucesso!");
        fetchTeamAgents();
        setIsDeleteDialogOpen(false);
        setCurrentAgent(null);
        onRefresh(); // Atualizar lista de equipes na página principal
      }
    } catch (error) {
      toast.error("Erro ao remover agente");
    }
  };

  const confirmDeleteAgent = (agent: Agent) => {
    setCurrentAgent(agent);
    setIsDeleteDialogOpen(true);
  };

const fetchUsers = async () => {
  const [response, error] = await agentService.getAll({
    search: {
      s_name: searchQuery
    },
  }, 1, 30);

  if (error) {
    toast.error("Erro ao buscar usuários");
    return;
  }

  setFilteredUsers(response?.result || []);
};

useEffect(() => {
  fetchUsers();
}, [searchQuery]);

  return (
    <div className="space-y-6">
      {/* Seção de Adicionar Novo Agente */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Gerenciar Agentes</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Buscar Usuário</Label>
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Digite o nome do usuário..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {searchQuery && (
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                {filteredUsers
                .filter((user) => !teamAgents.some((agent) => agent._id === user._id))
                .map((user) => (
                  <Card
                    key={user._id}
                    className={`p-3 cursor-pointer hover:bg-muted transition-colors ${
                      selectedUser.includes(user) ? "bg-muted" : ""
                    }`}
                    onClick={() => setSelectedUser([...selectedUser, user])}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      {selectedUser.includes(user) && (
                        <Badge variant="secondary">Selecionado</Badge>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          

          <Button
            onClick={handleAddAgent}
            disabled={isAddingAgent || !selectedUser}
            keyLoader="addAgent"
            className="w-full"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Adicionar Agente
          </Button>
        </div>
      </div>

    

      {/* Lista de Agentes da Equipe */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Agentes da Equipe</h3>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-2">
            {teamAgents.map((agent) => (
              <Card key={agent._id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{agent.name}</p>
                    <p className="text-sm text-muted-foreground">{agent.email}</p>
                    <div className="flex gap-2 mt-2">
                      {agent.permissions.map((permission) => (
                        <Badge key={permission} variant="secondary">
                          {permission.includes(AgentRole.APP_ADMIN) ? "Administrador" : "Agente"}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => confirmDeleteAgent(agent)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            {teamAgents.length === 0 && (
              <p className="text-center text-muted-foreground py-4">
                Nenhum agente nesta equipe
              </p>
            )}
          </div>
        )}
      </div>

      {/* Dialog de confirmação de exclusão */}
      <DeleteTeamDialog
        isOpen={isDeleteDialogOpen}
        teamName={currentAgent?.name || "este agente"}
        onConfirm={handleRemoveAgent}
        onClose={() => setIsDeleteDialogOpen(false)}
      />
    </div>
  );
}
